/**
 * A learner's results for one quiz / exam — /learn/exams/:examId/results
 *
 * Every attempt (how many, when, marks, pass / fail), and for the attempt being viewed the full
 * question-wise breakdown: all options, their answer, the correct answer and the explanation.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabLabel } from '../../components/shell/TabsContext';
import RecordPage from '../../components/shell/RecordPage';
import { StatTiles } from '../../components/shell/Page';
import { StatusBadge } from '../../components/datagrid/DataGrid';
import AnswerReview from '../../components/questions/AnswerReview';
import AttemptsTable, { useAttempts, formatDuration } from '../../components/exams/AttemptsTable';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ListChecks, Target, Repeat, Clock, Play, ArrowLeft, Trophy } from '@phosphor-icons/react';

export default function ExamResults() {
  const { examId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const courseId = location.state?.courseId;
  const [exam, setExam] = useState(null);
  const [selectedId, setSelectedId] = useState(location.state?.attemptId || null);
  const { attempts, loading, reload, stats } = useAttempts(api, examId);
  useTabLabel(exam?.title ? `Results: ${exam.title}` : 'Quiz results');

  const loadExam = useCallback(async () => {
    try { setExam((await api.get(`/exams/${examId}`)).data); }
    catch { toast.error('Could not load the quiz'); }
  }, [api, examId]);
  useEffect(() => { loadExam(); }, [loadExam]);

  const selected = useMemo(() => attempts.find((a) => a.id === selectedId) || attempts[0] || null, [attempts, selectedId]);
  const settings = exam?.settings || {};
  const passMark = settings.passing_score ?? 60;
  const maxAttempts = settings.max_attempts ?? 1;
  const attemptsLeft = Math.max(0, maxAttempts - stats.count);
  const back = () => navigate(courseId ? `/learn/course/${courseId}` : '/learn');

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="exam-results-page"
        title={exam?.title || 'Quiz results'}
        subtitle={exam ? `Your attempts and answers${exam.description ? ` · ${exam.description}` : ''}` : 'Loading…'}
        badge={stats.count > 0 && <StatusBadge value={stats.passed ? 'passed' : 'failed'} />}
        onBack={back}
        onRefresh={() => { reload(); loadExam(); }}
        refreshing={loading}
        loading={loading && !attempts.length && !exam}
        findable={false}
        primary={attemptsLeft > 0 ? [
          { key: 'retake', label: stats.count ? 'Try again' : 'Start quiz', icon: Play, onClick: () => navigate(`/learn/exams/${examId}`, { state: { courseId } }), testid: 'results-retake' },
        ] : []}
        secondary={[
          { key: 'back', label: courseId ? 'Back to course' : 'Back to dashboard', icon: ArrowLeft, variant: 'outline', onClick: back },
        ]}
        maxWidth="none"
      >
        <StatTiles items={[
          { label: 'Attempts', value: `${stats.count}${maxAttempts ? ` / ${maxAttempts}` : ''}`, icon: Repeat },
          { label: 'Best score', value: stats.best === null ? '—' : `${stats.best.toFixed(0)}%`, icon: Trophy, iconClass: 'bg-violet-100 text-violet-700' },
          { label: 'Latest score', value: stats.latest === null ? '—' : `${stats.latest.toFixed(0)}%`, icon: Target, iconClass: 'bg-sky-100 text-sky-700' },
          { label: 'Pass mark', value: `${Math.round(passMark)}%`, icon: ListChecks, iconClass: stats.passed ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700' },
        ]} />

        <section className="space-y-2">
          <h3 className="font-heading text-[15px] font-semibold">Your attempts</h3>
          <AttemptsTable attempts={attempts} selectedId={selected?.id} onSelect={(a) => setSelectedId(a.id)} passMark={passMark} maxAttempts={maxAttempts} />
          {attempts.length > 1 && <p className="field-hint">Pick an attempt to see the answers you gave that time.</p>}
          {attemptsLeft === 0 && stats.count > 0 && <p className="field-hint">You have used all {maxAttempts} attempt{maxAttempts === 1 ? '' : 's'}.</p>}
        </section>

        {selected && (
          <section className="space-y-3">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h3 className="font-heading text-[15px] font-semibold">Attempt #{selected.attempt_number} · answers</h3>
              <span className="text-sm text-muted-foreground">
                {selected.score}/{selected.max_score} marks · {(selected.percentage || 0).toFixed(0)}% ·{' '}
                {(selected.answers_detail || []).filter((a) => a.is_correct).length} of {(selected.answers_detail || []).length} correct ·{' '}
                <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{formatDuration(selected.time_taken_seconds)}</span>
              </span>
            </div>
            {(selected.answers_detail || []).length === 0 ? (
              <p className="text-[13px] text-muted-foreground">The answers for this attempt are not available.</p>
            ) : (
              <div className="space-y-3">
                {selected.answers_detail.map((ad, i) => <AnswerReview key={ad.question_id || i} detail={ad} index={i} />)}
              </div>
            )}
          </section>
        )}

        {!loading && attempts.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center" data-testid="results-none">
            <p className="text-[13px] text-muted-foreground mb-3">You haven't attempted this quiz yet.</p>
            <Button onClick={() => navigate(`/learn/exams/${examId}`, { state: { courseId } })}><Play className="w-4 h-4" /> Start quiz</Button>
          </div>
        )}
      </RecordPage>
    </DashboardLayout>
  );
}
